import { useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter, DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, pointerWithin,
  useDroppable, useSensor, useSensors, type CollisionDetection, type DragMoveEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { ChevronRight, FolderUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { LayerIcon } from './LayerIcon';
import { ROOT_DROP_ID, projectLayerDrop, type DropPosition, type FlatLayer, type LayerTreeItem } from './layerTreeMoves';
import type { LayerOrdering } from './useLayerOrdering';

type Projection = ReturnType<typeof projectLayerDrop>;
const INDENT = 20;
const noSortingAnimation = () => null;
const INTERACTIVE_CONTROLS = 'button, input, textarea, select, a, label[for], [role="button"], [role="checkbox"], [role="switch"], [role="slider"], [role="menuitem"], [contenteditable]:not([contenteditable="false"])';
function canStartRowDrag(event: { target: EventTarget | null; currentTarget: EventTarget | null }) {
  return event.target instanceof Element && event.currentTarget instanceof Element &&
    event.currentTarget.contains(event.target) && !event.target.closest(INTERACTIVE_CONTROLS);
}
const keyboardCoordinates: KeyboardCoordinateGetter = (event, args) => {
  if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
    return { ...args.currentCoordinates, x: args.currentCoordinates.x + (event.code === 'ArrowLeft' ? -INDENT : INDENT) };
  }
  return sortableKeyboardCoordinates(event, args);
};

function RootDropTarget({ active }: { active: boolean }) {
  const { t } = useTranslation('project');
  const { setNodeRef, isOver } = useDroppable({ id: ROOT_DROP_ID, disabled: !active });
  return <div ref={setNodeRef} aria-hidden={!active} className={cn(
    'flex items-center gap-2 rounded-md border border-dashed px-2 text-xs text-slate-500 dark:text-stone-400',
    active ? 'sticky bottom-0 z-20 mt-2 h-8 bg-white dark:bg-stone-900' : 'h-0 overflow-hidden border-0',
    isOver && 'border-sky-500 bg-sky-50 dark:bg-sky-950/30',
  )}>
    <FolderUp className='size-4' />{t('viewerLayers.moveToRoot')}
  </div>;
}

function SortableLayerRow({ item, disabled, draggable, projection, indicatorDepth, renderLayer }: {
  item: FlatLayer; disabled: boolean; draggable: boolean; projection: Projection; indicatorDepth: number;
  renderLayer: (layer: LayerTreeItem, depth: number) => ReactNode;
}) {
  const { t } = useTranslation('project');
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: item.layer.id, disabled, transition: null, attributes: { role: 'group' },
  });
  const targeted = projection?.targetId === item.layer.id;
  const position = targeted ? projection.position : null;
  return <div
    ref={setNodeRef}
    {...(draggable ? attributes : {})}
    aria-label={draggable ? t('viewerLayers.dragLayer', { name: item.layer.name }) : undefined}
    onMouseDown={disabled ? undefined : (event) => { if (canStartRowDrag(event)) listeners?.onMouseDown?.(event); }}
    onTouchStart={disabled ? undefined : (event) => { if (canStartRowDrag(event)) listeners?.onTouchStart?.(event); }}
    onKeyDown={disabled ? undefined : (event) => { if (event.target === event.currentTarget) listeners?.onKeyDown?.(event); }}
    data-layer-id={item.layer.id}
    data-drop-position={position ?? undefined}
    style={{ marginLeft: item.depth * INDENT }}
    className={cn('relative rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500',
      draggable && !disabled && 'cursor-grab active:cursor-grabbing',
      isDragging && 'opacity-30', position === 'inside' && 'bg-sky-50 ring-2 ring-sky-500 dark:bg-sky-950/30')}
  >
    {(position === 'before' || position === 'after') && <div
      aria-hidden='true'
      style={{ left: (indicatorDepth - item.depth) * INDENT }}
      className={cn('pointer-events-none absolute inset-x-0 z-10 h-0.5 rounded bg-sky-500', position === 'before' ? 'top-0' : 'bottom-0')}
    />}
    {renderLayer(item.layer, item.depth)}
  </div>;
}

export function LayerTree({ ordering, canManage, selectionMode, expandedLayers, groupByType, renderLayer,
  onToggleLayerType, isLayerSelected, isLayerIndeterminate, onExpandLayer }: {
  ordering: LayerOrdering; canManage: boolean; selectionMode: boolean; expandedLayers: Set<string>;
  groupByType: boolean; onExpandLayer?: (id: string) => void;
  renderLayer: (layer: LayerTreeItem, depth: number) => ReactNode;
  onToggleLayerType: (type: LayerTreeItem['type']) => void;
  isLayerSelected: (id: string) => boolean; isLayerIndeterminate: (id: string) => boolean;
}) {
  const { t } = useTranslation('project');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [projection, setProjection] = useState<Projection>(null);
  const pointerPosition = useRef<{ x: number; y: number } | null>(null);
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: keyboardCoordinates }),
  );
  const disabled = !canManage || selectionMode || ordering.isPending;
  const byId = useMemo(() => new Map(ordering.items.map((item) => [item.layer.id, item])), [ordering.items]);
  const activeItem = activeId ? byId.get(activeId) : undefined;
  const visible = useMemo(() => {
    const hidden = new Set<string>();
    return ordering.items.filter((item) => {
      const parentHidden = item.parentId !== null && hidden.has(item.parentId);
      const draggingParent = activeId !== null && item.parentId === activeId;
      const parentCollapsed = item.parentId !== null && !selectionMode && !expandedLayers.has(item.parentId);
      if (parentHidden || draggingParent || parentCollapsed) {
        hidden.add(item.layer.id);
        return false;
      }
      return true;
    });
  }, [ordering.items, expandedLayers, selectionMode, activeId]);
  const groups = useMemo(() => {
    if (!groupByType || selectionMode) return [{ type: null, items: visible }];
    const types = new Map<string, FlatLayer[]>();
    let rootType: string;
    for (const item of visible) {
      if (item.depth === 0) rootType = item.layer.type;
      const list = types.get(rootType!) ?? [];
      list.push(item);
      types.set(rootType!, list);
    }
    return [...types].sort(([a], [b]) => a.localeCompare(b)).map(([type, items]) => ({ type, items }));
  }, [groupByType, selectionMode, visible]);
  const displayed = groups.flatMap((group) => group.type && collapsedTypes.has(group.type) ? [] : group.items);
  // "After a folder" means after its entire visible subtree.
  const visualProjection = useMemo(() => {
    if (projection?.position !== 'after') return projection;
    const index = displayed.findIndex((item) => item.layer.id === projection.targetId);
    if (index < 0) return projection;
    let last = index;
    while (last + 1 < displayed.length && displayed[last + 1].depth > displayed[index].depth) last++;
    return { ...projection, targetId: displayed[last].layer.id };
  }, [projection, displayed]);
  const collisionDetection: CollisionDetection = (args) => {
    pointerPosition.current = args.pointerCoordinates;
    const droppableContainers = args.droppableContainers.filter((container) => {
      if (container.id === args.active.id) return false;
      let item = byId.get(String(container.id));
      while (item?.parentId) {
        if (item.parentId === args.active.id) return false;
        item = byId.get(item.parentId);
      }
      return true;
    });
    const filtered = { ...args, droppableContainers };
    // Pointer drops outside the list cancel; keyboard movement uses row centres.
    return args.pointerCoordinates ? pointerWithin(filtered) : closestCenter(filtered);
  };
  const getProjection = (event: DragMoveEvent): Projection => {
    if (disabled || !event.over) return null;
    const id = String(event.active.id);
    const targetId = String(event.over.id);
    const source = byId.get(id);
    const target = byId.get(targetId);
    if (!source) return null;
    if (targetId === ROOT_DROP_ID) return projectLayerDrop(ordering.items, id, targetId, 'inside', groupByType);
    if (!target) return null;
    const keyboard = event.activatorEvent instanceof KeyboardEvent;
    let position: DropPosition;
    if (keyboard) {
      position = target.layer.type === 'GROUP' && event.delta.x >= INDENT
        ? 'inside' : ordering.items.indexOf(source) < ordering.items.indexOf(target) ? 'after' : 'before';
    } else {
      // The pointer can start anywhere along the row, and the sidebar may auto-scroll.
      const y = pointerPosition.current?.y;
      if (y === undefined) return null;
      const ratio = (y - event.over.rect.top) / Math.max(event.over.rect.height, 1);
      position = target.layer.type === 'GROUP' && ratio > 0.25 && ratio < 0.75
        ? 'inside' : ratio < 0.5 ? 'before' : 'after';
    }
    const outdent = position === 'inside' ? 0 : Math.max(0, Math.round(-event.delta.x / INDENT));
    return projectLayerDrop(ordering.items, id, targetId, position, groupByType, outdent);
  };
  const reset = () => { setActiveId(null); setProjection(null); };
  const preview = (event: DragMoveEvent) => {
    const next = getProjection(event);
    // Heavy layer rows don't need a React update on every pointer pixel.
    setProjection((previous) => previous?.targetId === next?.targetId && previous?.position === next?.position &&
      previous?.parentId === next?.parentId ? previous : next);
  };
  return <DndContext
    sensors={sensors}
    collisionDetection={collisionDetection}
    autoScroll={{ threshold: { x: 0, y: 0.15 } }}
    accessibility={{
      screenReaderInstructions: { draggable: t('viewerLayers.dragInstructions') },
      announcements: {
        onDragStart: ({ active }) => t('viewerLayers.dragPickedUp', { name: byId.get(String(active.id))?.layer.name }),
        onDragOver: ({ active, over }) => over ? t('viewerLayers.dragOver', {
          name: byId.get(String(active.id))?.layer.name,
          target: over.id === ROOT_DROP_ID ? t('viewerLayers.rootLayers') : byId.get(String(over.id))?.layer.name,
        }) : undefined,
        onDragEnd: ({ active, over }) => t(over ? 'viewerLayers.dragDropped' : 'viewerLayers.dragCanceled', {
          name: byId.get(String(active.id))?.layer.name,
        }),
        onDragCancel: ({ active }) => t('viewerLayers.dragCanceled', { name: byId.get(String(active.id))?.layer.name }),
      },
    }}
    onDragStart={({ active }) => { if (!disabled) setActiveId(String(active.id)); }}
    onDragMove={preview}
    onDragOver={preview}
    onDragCancel={reset}
    onDragEnd={(event) => {
      const destination = getProjection(event);
      reset();
      if (destination) {
        if (destination.parentId) onExpandLayer?.(destination.parentId);
        ordering.move(String(event.active.id), destination.parentId, destination.orderedSiblingIds);
      }
    }}
  >
    <SortableContext items={displayed.map((item) => item.layer.id)} strategy={noSortingAnimation}>
      {groups.map(({ type, items }) => <div key={type ?? 'all'} className={type ? 'mb-4' : undefined}>
        {type && <div className='mb-2 flex items-center justify-between pr-1.5'>
          <button type='button' className='flex items-center gap-1' aria-expanded={!collapsedTypes.has(type)}
            onClick={() => setCollapsedTypes((previous) => {
              const next = new Set(previous);
              if (next.has(type)) next.delete(type); else next.add(type);
              return next;
            })}>
            <ChevronRight className={cn('size-4 transition-transform', !collapsedTypes.has(type) && 'rotate-90')} />
            <span className='text-xs font-semibold text-slate-700 dark:text-white'>
              {t(`viewerLayers.layerType${type.charAt(0) + type.slice(1).toLowerCase()}` as any)}
            </span>
          </button>
          <Checkbox checked={items.filter((item) => item.depth === 0).some((item) => isLayerSelected(item.layer.id) || isLayerIndeterminate(item.layer.id))}
            indeterminate={!items.filter((item) => item.depth === 0).every((item) => isLayerSelected(item.layer.id))}
            aria-label={t(`viewerLayers.layerType${type.charAt(0) + type.slice(1).toLowerCase()}` as any)}
            onCheckedChange={() => onToggleLayerType(type as LayerTreeItem['type'])} />
        </div>}
        {(!type || !collapsedTypes.has(type)) && items.map((item) => <SortableLayerRow key={item.layer.id}
          item={item} disabled={disabled || item.layer.isEphemeral}
          draggable={canManage && !selectionMode && !item.layer.isEphemeral}
          projection={visualProjection}
          indicatorDepth={projection?.parentId ? (byId.get(projection.parentId)?.depth ?? -1) + 1 : 0}
          renderLayer={renderLayer} />)}
      </div>)}
    </SortableContext>
    <RootDropTarget active={!!activeId} />
    {createPortal(<DragOverlay dropAnimation={null}>
      {activeItem && <div data-layer-drag-preview={activeItem.layer.id} className='flex h-8 items-center gap-2 rounded-md border border-slate-200 bg-white px-2 text-sm shadow-lg dark:border-stone-700 dark:bg-stone-900'>
        <LayerIcon type={activeItem.layer.type} />
        <span className='truncate'>{activeItem.layer.name}</span>
      </div>}
    </DragOverlay>, document.body)}
  </DndContext>;
}
